import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";

import { EntityKind } from "models/entityKind";
import { PolygonFilter } from "models/filter";

import { useGroupByState } from "components/groupBy/group-by.context";
import { useSelectedCategory } from "components/groupBy/hooks";

import { useFieldGroupDataQuery } from "../queries";

const MODAL_QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000,
  refetchOnMount: true,
  refetchOnWindowFocus: false
};

export interface UseFieldGroupDataParams {
  fieldGroup: string;
  isVisible: boolean;
}

/**
 * Fetches field group data based on current filter, entity kind, and map extent.
 */
export function useFieldGroupData({ fieldGroup, isVisible }: UseFieldGroupDataParams) {
  const { entityKind } = useGroupByState();
  const { isOrgDefinedColumn, isProjectDefinedColumn } = useSelectedCategory();
  const isOrg = isOrgDefinedColumn;

  const projectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  const wellFilterId = useSelector((state: RootState) => state.filter.filterId);
  const facilityFilterId = useSelector(
    (state: RootState) => state.facilityFilter.filterId
  );
  const filterId =
    entityKind === EntityKind.Well ? wellFilterId?.id : facilityFilterId?.id;
  const polygonFilter = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.filter.polygonFilter
      : state.facilityFilter.polygonFilter
  );

  // Use projectId only if this is a project-defined column
  const effectiveProjectId = isProjectDefinedColumn ? projectId : null;

  const query = useFieldGroupDataQuery({
    fieldGroup,
    entityKind,
    isOrg,
    filterId: filterId ?? "",
    polygonFilter,
    projectId: effectiveProjectId,
    enabled: isVisible && !!fieldGroup && !!filterId,
    queryConfig: MODAL_QUERY_CONFIG,
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    entityKind,
    isOrg,
    effectiveProjectId,
    filterId,
    polygonFilter: polygonFilter as PolygonFilter | null
  };
}
